import { Icon } from "components/ui/Icon";
import { Toolbar } from "components/ui/Toolbar";
import { useTextmenuCommands } from "./hooks/useTextmenuCommands";
import { useTextmenuStates } from "./hooks/useTextmenuStates";
import { BubbleMenu, Editor } from "@tiptap/react";
import { memo, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { FontSizePicker } from "./components/FontSizePicker";
import { useTextmenuContentTypes } from "./hooks/useTextmenuContentTypes";
import { ContentTypePicker } from "./components/ContentTypePicker";
import { AIDropdown } from "./components/AIDropdown";
import FindeZitate from "components/General/FindeZitate";
import IntegrateZitat from "components/General/IntegrateZitat";
import {
  getSelectedText,
  insertTextBelow,
  replaceSelectedText,
} from "lib/utils/editor";
import axiosErrorHandler from "lib/utils/axiosErrorHandler";
import improveText from "lib/writing-ai/improveText";
import KiPrompt from "components/General/KiPrompt";

// We memorize the button so each button is not rerendered
// on every editor state change
const MemoButton = memo(Toolbar.Button);
const MemoFontSizePicker = memo(FontSizePicker);
const MemoContentTypePicker = memo(ContentTypePicker);

export type TextMenuProps = {
  editor: Editor;
};

export const TextMenu = ({ editor }: TextMenuProps) => {
  const commands = useTextmenuCommands(editor);
  const states = useTextmenuStates(editor);
  const blockOptions = useTextmenuContentTypes(editor);
  const [improvedText, setImprovedText] = useState("");
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  const handleAIAction = async (aim: string, style?: string) => {
    if (!loading) {
      try {
        setLoading(true);
        let text = getSelectedText(editor);
        let resp = await improveText({ aim, style, text });
        setImprovedText(resp.improvedText);
        setVisible(true);
      } catch (e) {
        axiosErrorHandler(e);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleKiPromptClose = () => {
    setImprovedText("");
    setVisible(false);
  };

  return (
    <BubbleMenu
      tippyOptions={{
        popperOptions: { placement: "bottom-start" },
        appendTo: document.getElementById("editor-container"),
      }}
      editor={editor}
      pluginKey="textMenu"
      shouldShow={states.shouldShow}
      updateDelay={100}
    >
      <Toolbar.Wrapper>
        {/* <MemoContentTypePicker options={blockOptions} />
        <MemoFontFamilyPicker onChange={commands.onSetFont} value={states.currentFont || ''} />
        */}
        <KiPrompt
          value={improvedText}
          handleClose={handleKiPromptClose}
          handleReplace={() => {
            replaceSelectedText(editor, improvedText);
            setVisible(false);
          }}
          handleInsertBelow={() => {
            insertTextBelow(editor, improvedText);
            setVisible(false);
          }}
          setVisible={setVisible}
          visible={visible}
        />
        <FindeZitate editor={editor}>
          <MemoButton variant="ghost" buttonSize="medium">
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 9.5V8H3.5C2.67157 8 2 8.67157 2 9.5M4.4 11H8.4C8.96005 11 9.24008 11 9.45399 10.891C9.64215 10.7951 9.79513 10.6422 9.89101 10.454C10 10.2401 10 9.96005 10 9.4V2.6C10 2.03995 10 1.75992 9.89101 1.54601C9.79513 1.35785 9.64215 1.20487 9.45399 1.10899C9.24008 1 8.96005 1 8.4 1H4.4C3.55992 1 3.13988 1 2.81901 1.16349C2.53677 1.3073 2.3073 1.53677 2.16349 1.81901C2 2.13988 2 2.55992 2 3.4V8.6C2 9.44008 2 9.86012 2.16349 10.181C2.3073 10.4632 2.53677 10.6927 2.81901 10.8365C3.13988 11 3.55992 11 4.4 11Z"
                stroke="#7F7F7F"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Finde Zitate
          </MemoButton>
        </FindeZitate>
        <Toolbar.Divider />
        <IntegrateZitat editor={editor}>
          <MemoButton variant="ghost" buttonSize="medium">
            <svg
              width="13"
              height="13"
              viewBox="0 0 13 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.04171 3.79167H2.81671C2.20998 3.79167 1.90662 3.79167 1.67488 3.90974C1.47104 4.01361 1.30531 4.17933 1.20145 4.38318C1.08337 4.61491 1.08337 4.91828 1.08337 5.525V7.475C1.08337 8.08172 1.08337 8.38509 1.20145 8.61682C1.30531 8.82066 1.47104 8.98639 1.67488 9.09026C1.90662 9.20833 2.20998 9.20833 2.81671 9.20833H7.04171M9.20837 3.79167H10.1834C10.7901 3.79167 11.0935 3.79167 11.3252 3.90974C11.529 4.01361 11.6948 4.17933 11.7986 4.38318C11.9167 4.61491 11.9167 4.91828 11.9167 5.525V7.475C11.9167 8.08172 11.9167 8.38509 11.7986 8.61682C11.6948 8.82066 11.529 8.98639 11.3252 9.09026C11.0935 9.20833 10.7901 9.20833 10.1834 9.20833H9.20837M9.20837 11.375L9.20837 1.625M10.5625 1.625L7.85421 1.625M10.5625 11.375L7.85421 11.375"
                stroke="#7F7F7F"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Integriere Zitat
          </MemoButton>
        </IntegrateZitat>
        <Toolbar.Divider />
        <MemoContentTypePicker options={blockOptions} />
        <Toolbar.Divider />
        <AIDropdown onImprove={handleAIAction} loading={loading} />
        <Toolbar.Divider />
        <MemoButton
          tooltip="Bold"
          tooltipShortcut={["Mod", "B"]}
          onClick={commands.onBold}
          active={states.isBold}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.06699 13.014C4.29499 13.014 3.84399 12.563 3.84399 11.776V4.36005C3.84399 3.57405 4.29399 3.12305 5.06699 3.12305H8.56699C10.516 3.12305 11.698 4.06605 11.698 5.61105C11.698 6.71105 10.905 7.58105 9.74999 7.73105V7.79205C11.151 7.86005 12.15 8.83805 12.15 10.178C12.15 11.894 10.803 13.015 8.68399 13.015H5.06699V13.014ZM6.30499 7.20405H7.64499C8.66999 7.20405 9.27799 6.74505 9.27799 5.97905C9.27799 5.24105 8.72499 4.81105 7.79499 4.81105H6.30499V7.20305V7.20405ZM6.30499 11.346H7.90499C9.05299 11.346 9.64699 10.874 9.64699 9.99905C9.64699 9.14505 9.00499 8.67905 7.84299 8.67905H6.30499V11.346Z"
              fill="#7F7F7F"
            />
          </svg>
        </MemoButton>
        <MemoButton
          tooltip="Italic"
          tooltipShortcut={["Mod", "I"]}
          onClick={commands.onItalic}
          active={states.isItalic}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.01998 13.0141C4.62298 13.0141 4.34998 12.7741 4.34998 12.3841C4.34998 12.0021 4.61598 11.7701 5.01298 11.7701H6.41998L8.01298 4.36607H6.61198C6.21598 4.36607 5.94198 4.12707 5.94198 3.73707C5.94198 3.35407 6.20898 3.12207 6.60498 3.12207H10.987C11.384 3.12207 11.65 3.35407 11.65 3.73707C11.65 4.12707 11.377 4.36707 10.98 4.36707H9.58598L7.98598 11.7701H9.39498C9.79098 11.7701 10.058 12.0021 10.058 12.3851C10.058 12.7751 9.78398 13.0141 9.38798 13.0141H5.01998Z"
              fill="#7F7F7F"
            />
          </svg>
        </MemoButton>
        <MemoButton
          tooltip="Underline"
          tooltipShortcut={["Mod", "U"]}
          onClick={commands.onUnderline}
          active={states.isUnderline}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.99999 13.1908C7.15699 13.1908 6.42799 13.0388 5.81199 12.7328C5.22589 12.4449 4.73325 11.997 4.39099 11.4408C4.05799 10.8898 3.89099 10.2498 3.89099 9.52085V3.72385C3.89099 3.48185 3.96199 3.29285 4.10399 3.15585C4.24399 3.01485 4.43399 2.94385 4.67099 2.94385C4.91699 2.94385 5.11099 3.01485 5.25099 3.15585C5.39799 3.29285 5.47099 3.48185 5.47099 3.72385V9.37685C5.47099 9.86485 5.56899 10.2908 5.76499 10.6548C5.96499 11.0148 6.25499 11.2948 6.63299 11.4958C7.01099 11.6958 7.46699 11.7958 7.99999 11.7958C8.79799 11.7958 9.41699 11.5758 9.85999 11.1338C10.306 10.6918 10.53 10.1058 10.53 9.37685V3.72385C10.53 3.48185 10.6 3.29285 10.741 3.15585C10.883 3.01485 11.071 2.94385 11.309 2.94385C11.555 2.94385 11.749 3.01485 11.889 3.15585C12.036 3.29285 12.109 3.48185 12.109 3.72385V9.51985C12.109 10.2488 11.942 10.8888 11.609 11.4398C11.277 11.9918 10.8 12.4218 10.181 12.7318C9.56499 13.0378 8.83899 13.1898 7.99999 13.1898V13.1908ZM4.37699 15.3648C4.31272 15.3663 4.24879 15.3551 4.18889 15.3317C4.12899 15.3084 4.07431 15.2734 4.02799 15.2288C3.98319 15.1841 3.94802 15.1307 3.92463 15.0719C3.90124 15.0131 3.89014 14.9501 3.89199 14.8868C3.89199 14.7498 3.93699 14.6368 4.02799 14.5448C4.07423 14.5001 4.12888 14.4649 4.18878 14.4414C4.24868 14.4179 4.31265 14.4065 4.37699 14.4078H11.617C11.757 14.4078 11.874 14.4538 11.965 14.5448C12.0116 14.5885 12.0484 14.6416 12.0731 14.7006C12.0977 14.7595 12.1096 14.823 12.108 14.8868C12.108 15.0228 12.06 15.1368 11.965 15.2278C11.9193 15.2733 11.8647 15.3089 11.8047 15.3325C11.7447 15.356 11.6804 15.3671 11.616 15.3648H4.37699Z"
              fill="#7F7F7F"
            />
          </svg>
        </MemoButton>
        <MemoFontSizePicker
          onChange={commands.onSetFontSize}
          value={states.currentSize || ""}
        />
        <MemoButton
          tooltip="Strikehrough"
          tooltipShortcut={["Mod", "Shift", "S"]}
          onClick={commands.onStrike}
          active={states.isStrike}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.97305 13.1908C7.3801 13.1981 6.78965 13.1128 6.22305 12.9378C5.7441 12.7921 5.29977 12.5505 4.91705 12.2278C4.58188 11.9346 4.33586 11.5531 4.20705 11.1268C4.16638 10.9891 4.14551 10.8464 4.14505 10.7028C4.14505 10.4978 4.20605 10.3338 4.32905 10.2108C4.39814 10.1477 4.47904 10.0989 4.56708 10.0671C4.65512 10.0354 4.74857 10.0213 4.84205 10.0258C4.99705 10.0258 5.12205 10.0598 5.21805 10.1288C5.3335 10.2152 5.42701 10.3276 5.49105 10.4568C5.61905 10.7528 5.80105 11.0108 6.03805 11.2288C6.27505 11.4438 6.55805 11.6078 6.88605 11.7218C7.21405 11.8358 7.57605 11.8918 7.97305 11.8918C8.42305 11.8918 8.82005 11.8258 9.16305 11.6938C9.50805 11.5578 9.77705 11.3728 9.96905 11.1408C10.165 10.9038 10.263 10.6348 10.263 10.3338C10.263 9.95082 10.113 9.64582 9.81205 9.41782C9.51105 9.18582 9.01605 8.98782 8.32805 8.82282L7.04305 8.52982C6.10405 8.31982 5.40705 8.00182 4.95105 7.57282C4.50005 7.13982 4.27405 6.57482 4.27405 5.87782C4.27405 5.30382 4.43205 4.79582 4.74605 4.35382C5.06605 3.91082 5.50305 3.56682 6.05905 3.32082C6.61505 3.07082 7.25005 2.94482 7.96605 2.94482C8.56705 2.94482 9.11405 3.03482 9.60605 3.21182C10.103 3.38482 10.516 3.62682 10.844 3.93682C11.172 4.24682 11.391 4.60682 11.5 5.01682C11.5179 5.0744 11.5293 5.13375 11.534 5.19382C11.544 5.24882 11.548 5.30382 11.548 5.35782C11.5519 5.44506 11.5374 5.53213 11.5056 5.61342C11.4737 5.69472 11.4251 5.76844 11.363 5.82982C11.24 5.94882 11.073 6.00782 10.864 6.00782C10.577 6.00782 10.358 5.87582 10.208 5.61082C10.0915 5.33085 9.91933 5.07741 9.70205 4.86582C9.48893 4.65865 9.23191 4.50211 8.95005 4.40782C8.63358 4.29905 8.30066 4.24593 7.96605 4.25082C7.56005 4.25082 7.20005 4.31482 6.88605 4.44182C6.5953 4.54817 6.33814 4.73008 6.14105 4.96882C5.96519 5.19049 5.87091 5.46589 5.87405 5.74882C5.87405 6.10882 6.02205 6.39682 6.31805 6.61582C6.61905 6.82982 7.10205 7.01482 7.76805 7.16982L8.93705 7.43682C9.95305 7.66382 10.693 7.98982 11.158 8.41382C11.628 8.83382 11.862 9.41182 11.862 10.1498C11.862 10.7608 11.7 11.2968 11.377 11.7568C11.057 12.2128 10.607 12.5658 10.023 12.8168C9.44505 13.0668 8.76105 13.1918 7.97305 13.1918V13.1908ZM2.55205 8.38582C2.50239 8.38673 2.45307 8.37744 2.40714 8.35852C2.36121 8.3396 2.31966 8.31145 2.28505 8.27582C2.21474 8.20513 2.1752 8.10953 2.17505 8.00982C2.17505 7.90982 2.21205 7.82282 2.28505 7.74982C2.31871 7.71256 2.35994 7.68294 2.406 7.66293C2.45205 7.64292 2.50185 7.633 2.55205 7.63382H13.442C13.546 7.63382 13.635 7.67282 13.708 7.74982C13.781 7.82282 13.818 7.90982 13.818 8.00982C13.818 8.10982 13.781 8.19882 13.708 8.27582C13.6734 8.31145 13.6319 8.3396 13.586 8.35852C13.54 8.37744 13.4907 8.38673 13.441 8.38582H2.55205Z"
              fill="#7F7F7F"
            />
          </svg>
        </MemoButton>
        <Toolbar.Divider />
        <Popover.Root>
          <Popover.Trigger asChild>
            <MemoButton tooltip="More options">
              <Icon name="MoreHorizontal" />
            </MemoButton>
          </Popover.Trigger>
          <Popover.Content side="top" asChild>
            <Toolbar.Wrapper>
              <MemoButton
                tooltip="Subscript"
                tooltipShortcut={["Mod", "."]}
                onClick={commands.onSubscript}
                active={states.isSubscript}
              >
                <Icon name="Subscript" />
              </MemoButton>
              <MemoButton
                tooltip="Superscript"
                tooltipShortcut={["Mod", ","]}
                onClick={commands.onSuperscript}
                active={states.isSuperscript}
              >
                <Icon name="Superscript" />
              </MemoButton>
              <Toolbar.Divider />
              <MemoButton
                tooltip="Align left"
                tooltipShortcut={["Shift", "Mod", "L"]}
                onClick={commands.onAlignLeft}
                active={states.isAlignLeft}
              >
                <Icon name="AlignLeft" />
              </MemoButton>
              <MemoButton
                tooltip="Align center"
                tooltipShortcut={["Shift", "Mod", "E"]}
                onClick={commands.onAlignCenter}
                active={states.isAlignCenter}
              >
                <Icon name="AlignCenter" />
              </MemoButton>
              <MemoButton
                tooltip="Align right"
                tooltipShortcut={["Shift", "Mod", "R"]}
                onClick={commands.onAlignRight}
                active={states.isAlignRight}
              >
                <Icon name="AlignRight" />
              </MemoButton>
              <MemoButton
                tooltip="Justify"
                tooltipShortcut={["Shift", "Mod", "J"]}
                onClick={commands.onAlignJustify}
                active={states.isAlignJustify}
              >
                <Icon name="AlignJustify" />
              </MemoButton>
            </Toolbar.Wrapper>
          </Popover.Content>
        </Popover.Root>
      </Toolbar.Wrapper>
    </BubbleMenu>
  );
};
